import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import { makeStyles } from "@material-ui/core/styles";
import { authDelete, authGet, authPost, authPut } from "../../lib";
import AccountAddressDialog from "./Components/AccountAddressDialog";

/**
 * One customer account — a port of the legacy account.php.
 *
 * Order history, addresses, sign-in details and the quick actions, in the same
 * order the legacy page had them.
 */

const STATUS_COLOR = {
  0: "183,28,28",
  1: "13,71,161",
  2: "224,168,0",
  3: "27,94,32",
  4: "106,27,154",
  5: "141,110,79",
};

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1360, margin: "24px auto 0", width: "100%" },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontSize: 17, fontWeight: 500 },
  spacer: { flex: 1 },
  stamp: { color: theme.palette.text.secondary, fontSize: 13 },
  wide: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  half: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  panel: { padding: theme.spacing(2) },
  panelTitle: {
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".02em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.25),
  },
  section: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    paddingBottom: 5,
    margin: theme.spacing(2.5, 0, 1.5),
    "&:first-child": { marginTop: 0 },
  },
  line: { fontSize: 13, lineHeight: 1.85 },
  label: { color: theme.palette.text.secondary },
  muted: { color: theme.palette.text.secondary },
  cell: { fontSize: 13, padding: "4px 10px" },
  headCell: { fontSize: 12, fontWeight: 600, padding: "6px 10px" },
  num: { fontVariantNumeric: "tabular-nums", textAlign: "right" },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
  addr: {
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: 3,
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    fontSize: 13,
    lineHeight: 1.6,
    position: "relative",
  },
  addrHead: { display: "flex", alignItems: "center", gap: 6, marginBottom: 4 },
  tag: {
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: ".04em",
    background: "rgba(49,81,121,0.10)",
    color: "#315179",
  },
  iconBtn: { padding: 2, color: "rgba(0,0,0,0.4)", "&:hover": { color: "#315179" } },
  delBtn: { padding: 2, color: "rgba(0,0,0,0.3)", "&:hover": { color: "#c62828" } },
  inline: { display: "flex", gap: theme.spacing(1), alignItems: "flex-start" },
  grow: { flex: 1 },
  state: {
    display: "inline-block",
    padding: "1px 7px",
    borderRadius: 3,
    fontSize: 11,
    fontWeight: 700,
  },
  notice: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1.5),
    borderRadius: 3,
    fontSize: 13,
    background: "rgba(27,94,32,0.10)",
    color: "#1b5e20",
  },
  warnBox: {
    padding: theme.spacing(1, 1.5),
    marginTop: theme.spacing(1),
    borderRadius: 3,
    fontSize: 12.5,
    lineHeight: 1.55,
    background: "rgba(224,168,0,0.14)",
    color: "#8a6100",
    border: "1px solid rgba(224,168,0,0.4)",
  },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  hint: { color: theme.palette.text.secondary, fontSize: 12, marginTop: 4 },
  loading: { padding: theme.spacing(6), textAlign: "center" },
}));

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleDateString();
};

/** The legacy list linked FedEx numbers straight to tracking. */
function Notes({ text }) {
  const classes = useStyles();
  const v = String(text ?? "");
  const m = /FedEx Tracking Number:\s*(\d+)/i.exec(v);
  if (!m) return <span>{v}</span>;
  return (
    <a
      className={classes.link}
      href={`https://www.fedex.com/fedextrack/?trknbr=${m[1]}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      {v}
    </a>
  );
}

export function AccountDetailPage() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orderId, setOrderId] = useState("");
  const [taxExempt, setTaxExempt] = useState(false);
  const [resaleId, setResaleId] = useState("");

  const [editingAddress, setEditingAddress] = useState(null);
  const [wishlist, setWishlist] = useState(null);

  const load = useCallback(async () => {
    setError("");
    const { data: res, error: err } = await authGet(`/accounts/${id}`);
    if (err) {
      setError(err.message || "Could not load that account.");
      return;
    }
    setData(res);
    setEmail(res.account?.email ?? "");
    setTaxExempt(!!Number(res.account?.tax_exempt));
    setResaleId(res.account?.resale_id ?? "");
    setPassword("");
  }, [id]);

  useEffect(() => {
    setData(null);
    setWishlist(null);
    load();
  }, [load]);

  const act = async (fn, okMessage) => {
    setBusy(true);
    setError("");
    setNotice("");
    const { data: res, error: err } = await fn();
    setBusy(false);

    if (err) {
      setError(err.message || "That did not work.");
      return null;
    }
    setNotice(typeof okMessage === "function" ? okMessage(res) : okMessage);
    load();
    return res;
  };

  const showWishlist = async () => {
    if (wishlist) {
      setWishlist(null);
      return;
    }
    setWishlist("loading");
    const { data: res } = await authGet(`/accounts/${id}/wishlist`);
    setWishlist(res?.rows ?? []);
  };

  const removeAddress = (a) => async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this address?")) return;
    await act(
      () => authDelete(`/accounts/${id}/addresses/${a.address_id}`),
      "Address deleted.",
    );
  };

  if (error && !data) {
    return (
      <Box className={classes.page}>
        <div className={classes.error}>{error}</div>
      </Box>
    );
  }
  if (!data) {
    return (
      <Box className={classes.page}>
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      </Box>
    );
  }

  const a = data.account;

  return (
    <Box className={classes.page}>
      <div className={classes.head}>
        <Button size="small" variant="outlined" onClick={() => history.push("/accounts")}>
          ← Accounts
        </Button>
        <Typography className={classes.title}>
          {[a.first_name, a.mi, a.last_name].filter(Boolean).join(" ") || a.email}
        </Typography>
        <span className={classes.stamp}>#{a.account_id}</span>
        {!!Number(a.tax_exempt) && (
          <span className={classes.tag} style={{ background: "rgba(27,94,32,0.12)", color: "#1b5e20" }}>
            TAX EXEMPT
          </span>
        )}
        <div className={classes.spacer} />
        <span className={classes.stamp}>Customer since {when(a.created_at)}</span>
      </div>

      {notice && <div className={classes.notice}>{notice}</div>}
      {error && <div className={classes.error}>{error}</div>}

      <Paper className={classes.wide}>
        <div className={classes.panelTitle}>
          Order History
          <div className={classes.spacer} />
          <Button
            size="small"
            variant="outlined"
            onClick={() => history.push(`/orders/new?account_id=${a.account_id}`)}
          >
            + New Order
          </Button>
        </div>

        {!data.orders.length ? (
          <div className={classes.muted} style={{ fontSize: 13 }}>No orders.</div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell} style={{ width: 100 }}>Order</TableCell>
                <TableCell className={classes.headCell} style={{ width: 110 }}>Date</TableCell>
                <TableCell className={`${classes.headCell} ${classes.num}`} style={{ width: 110 }}>
                  Total
                </TableCell>
                <TableCell className={classes.headCell} style={{ width: 110 }}>Status</TableCell>
                <TableCell className={classes.headCell}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.orders.map((o) => {
                const rgb = STATUS_COLOR[o.order_status_id] ?? "97,97,97";
                return (
                  <TableRow key={o.order_id} hover style={{ height: 36 }}>
                    <TableCell className={classes.cell}>
                      <span
                        className={classes.link}
                        onClick={() => history.push(`/orders/${o.order_id}`)}
                      >
                        {Number(o.is_preowned) ? "P" : ""}
                        {o.order_id}
                      </span>
                    </TableCell>
                    <TableCell className={classes.cell}>{when(o.order_time)}</TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {money(o.items_total)}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <span
                        className={classes.state}
                        style={{ background: `rgba(${rgb},0.16)`, color: `rgb(${rgb})` }}
                      >
                        {o.status || "Pending"}
                      </span>
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <Notes text={o.notes_customer} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <div className={classes.half}>
        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>
            Addresses
            <div className={classes.spacer} />
            <Button size="small" variant="outlined" onClick={() => setEditingAddress({})}>
              + Add
            </Button>
          </div>

          {!data.addresses.length ? (
            <div className={classes.muted} style={{ fontSize: 13 }}>No addresses.</div>
          ) : (
            data.addresses.map((ad) => (
              <div className={classes.addr} key={ad.address_id}>
                <div className={classes.addrHead}>
                  <strong>
                    {[ad.first_name, ad.mi, ad.last_name].filter(Boolean).join(" ")}
                  </strong>
                  {!!Number(ad.default_bill) && <span className={classes.tag}>BILLING</span>}
                  {!!Number(ad.default_ship) && <span className={classes.tag}>SHIPPING</span>}
                  <div className={classes.spacer} />
                  <IconButton
                    size="small"
                    className={classes.iconBtn}
                    title="Edit this address"
                    onClick={() => setEditingAddress(ad)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={classes.delBtn}
                    title="Delete this address"
                    onClick={removeAddress(ad)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </div>
                {ad.business_name && <div>{ad.business_name}</div>}
                <div>{ad.address1}</div>
                {ad.address2 && <div>{ad.address2}</div>}
                <div>
                  {ad.city}
                  {ad.city && ad.state ? ", " : ""}
                  {ad.state} {ad.zip} {ad.country}
                </div>
                {(ad.home_phone || ad.contact_phone) && (
                  <div className={classes.muted}>
                    {[ad.home_phone, ad.contact_phone].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            ))
          )}
        </Paper>

        <Paper className={classes.panel}>
          <div className={classes.section}>Account Info</div>
          <div className={classes.line}>
            <span className={classes.label}>Name:</span>{" "}
            {[a.first_name, a.mi, a.last_name].filter(Boolean).join(" ") || (
              <span className={classes.muted}>not set</span>
            )}
            {a.country ? ` · ${a.country}` : ""}
          </div>

          <div style={{ marginTop: 12 }}>
            <div className={classes.inline}>
              <TextField
                className={classes.grow}
                label="Sign-in email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                size="small"
                variant="outlined"
                style={{ marginTop: 6 }}
                disabled={busy || !email.trim() || email === a.email}
                onClick={() =>
                  // eslint-disable-next-line no-alert
                  window.confirm("Change this customer's sign-in email?") &&
                  act(() => authPut(`/accounts/${id}/email`, { email }), "Email changed.")
                }
              >
                Save
              </Button>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div className={classes.inline}>
              <TextField
                className={classes.grow}
                label="Reset password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                size="small"
                variant="outlined"
                style={{ marginTop: 6 }}
                disabled={busy || password.length < 6}
                onClick={() =>
                  // eslint-disable-next-line no-alert
                  window.confirm("Reset this customer's password?") &&
                  act(
                    () => authPut(`/accounts/${id}/password`, { password }),
                    "Password changed — no email sent.",
                  )
                }
              >
                {busy ? "…" : "Reset"}
              </Button>
            </div>
            <div className={classes.hint}>At least six characters.</div>
            <div className={classes.warnBox}>
              Email is not hooked up yet — the customer won't receive a reset
              password email.
            </div>
          </div>

          <div className={classes.section}>Link an Order</div>
          <div className={classes.inline}>
            <TextField
              className={classes.grow}
              label="Order number"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
            />
            <Button
              size="small"
              variant="outlined"
              style={{ marginTop: 6 }}
              disabled={busy || !orderId.trim()}
              onClick={async () => {
                const done = await act(
                  () => authPost(`/accounts/${id}/orders`, { order_id: Number(orderId) }),
                  (r) => `Order ${r.order_id} attached.`,
                );
                if (done) setOrderId("");
              }}
            >
              Add
            </Button>
          </div>

          <div className={classes.section}>Tax Status</div>
          {!data.taxControlAvailable ? (
            <div className={classes.muted} style={{ fontSize: 12.5 }}>
              This database has no tax_exempt column, so the setting is unavailable.
            </div>
          ) : (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={taxExempt}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setTaxExempt(next);
                      act(
                        () =>
                          authPut(`/accounts/${id}/tax-exempt`, {
                            tax_exempt: next ? 1 : 0,
                            resale_id: resaleId,
                          }),
                        next ? "Marked tax exempt." : "Tax exemption removed.",
                      );
                    }}
                  />
                }
                label={<strong>Tax exempt</strong>}
              />
              <div className={classes.hint}>
                Every order this customer places is charged $0.00 sales tax.
              </div>
              <div className={classes.inline} style={{ marginTop: 8 }}>
                <TextField
                  className={classes.grow}
                  label="Resale ID"
                  value={resaleId}
                  inputProps={{ maxLength: 64 }}
                  onChange={(e) => setResaleId(e.target.value)}
                />
                <Button
                  size="small"
                  variant="outlined"
                  style={{ marginTop: 6 }}
                  disabled={busy}
                  onClick={() =>
                    act(
                      () =>
                        authPut(`/accounts/${id}/tax-exempt`, {
                          tax_exempt: taxExempt ? 1 : 0,
                          resale_id: resaleId,
                        }),
                      "Resale ID saved.",
                    )
                  }
                >
                  Save
                </Button>
              </div>
            </>
          )}

          <div className={classes.section}>Customer Activity</div>
          {!data.hasWishlist ? (
            <div className={classes.muted} style={{ fontSize: 12.5 }}>
              No wishlist for this customer.
            </div>
          ) : (
            <>
              <Button size="small" className={classes.link} onClick={showWishlist}>
                {wishlist ? "Hide wishlist" : "View wishlist"}
              </Button>
              {wishlist === "loading" && (
                <div className={classes.muted} style={{ fontSize: 12.5 }}>Loading…</div>
              )}
              {Array.isArray(wishlist) && (
                <div style={{ fontSize: 12.5, lineHeight: 1.8, marginTop: 6 }}>
                  {!wishlist.length ? (
                    <span className={classes.muted}>Nothing on it.</span>
                  ) : (
                    wishlist.map((w) => (
                      <div key={w.wishlist_id}>
                        {w.label}{" "}
                        <span className={classes.muted}>({when(w.time_added)})</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </Paper>
      </div>

      {editingAddress && (
        <AccountAddressDialog
          accountId={id}
          address={editingAddress.address_id ? editingAddress : null}
          onClose={() => setEditingAddress(null)}
          onSaved={() => {
            setEditingAddress(null);
            setNotice("Address saved.");
            load();
          }}
        />
      )}
    </Box>
  );
}

export default AccountDetailPage;
